<template>
    <div :class="stateClasses" class="field">
        <label v-if="label" :for="name" class="field__label"> {{ label }} <span v-if="required">*</span> </label>
        <select
            v-bind="$attrs"
            :name="name"
            :id="name"
            :value="newValue"
            class="field__input field--custom-select select__wrapper select__wrapper--grey"
            ref="simpleSelectInput"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @change="onChange"
        >
            <option v-for="option in options" :key="option.key" :value="option.key">{{ option.value }}</option>
        </select>

        <template v-if="error">
            <span class="field__message">
                <small>{{ error }}</small>
            </span>
        </template>
    </div>
</template>

<script>
export default {
    name: 'SimpleSelectInput',
    props: {
        value: {
            type: [Number, String],
            default: null
        },
        label: {
            type: String,
            default: null,
            required: false
        },
        name: {
            type: String,
            default: '',
            required: true
        },
        required: {
            type: Boolean,
            default: false,
            required: false
        },
        focus: {
            type: Boolean,
            default: false,
            required: false
        },
        options: {
            type: Array,
            default: () => [],
            required: false
        },
        error: {
            type: String,
            default: null
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            newValue: this.value
        };
    },
    computed: {
        /**
         * Generate the state classes.
         *
         * @return {object}
         */
        stateClasses() {
            return {
                'has-error': this.error
            };
        }
    },
    /**
     * Component is mounted, set the focus on the input.
     */
    mounted() {
        this.autofocus();
    },
    watch: {
        /**
         * When v-model is changed:
         *   1. Set internal value.
         *   2. If it's invalid, validate again.
         *
         * @param {string} value
         */
        value(value) {
            this.newValue = value;
        },

        /**
         * Update the v-model and validate again whenever
         * internal value is changed.
         *
         * @param {string} value
         */
        newValue(value) {
            this.$emit('input', value);
        }
    },
    methods: {
        /**
         * Input's 'input' event listener, 'nextTick' is used to prevent event firing
         * before ui update, helps when using masks (Cleavejs and potentially others).
         *
         * @param {object} event
         */
        onInput(event) {
            this.$nextTick(() => {
                this.newValue = event.target.value;
            });
        },

        /**
         * Return a focus event to the parent component if you focus on the input.
         */
        onFocus() {
            this.$emit('focus');
        },

        /**
         * Return a blur event to the parent component if you blur the input.
         */
        onBlur() {
            this.$emit('blur', this.newValue);
        },

        /**
         * Return a change event to the parent component if you change the input.
         *
         * @param {object} event
         */
        onChange(event) {
            this.$nextTick(() => {
                this.newValue = event.target.value;
            });

            this.$emit('change', this.newValue);
        },

        /**
         * Put focus on the text input.
         */
        autofocus() {
            if (!this.focus) {
                return;
            }

            this.$refs.simpleSelectInput.focus();
        }
    }
};
</script>
